import { useState } from 'react';
import { Alert, Button, Input, Typography } from 'antd';
import { DeploymentUnitOutlined, LoginOutlined } from '@ant-design/icons';
import { issuerBase, issuerForHub } from './oidcConfig';

const { Title, Text } = Typography;

/**
 * The hub sign-in screen (owner decision OPSFL-PS2-OIDC = option a). The operator enters their hub
 * SLUG → the SPA derives the Keycloak realm `opsflow-<slug>` and redirects to that realm's issuer.
 * Rendered only in real-auth mode when no hub is chosen yet (mock mode + a full VITE_OIDC_ISSUER both
 * bypass this).
 */
export function HubLogin({ onSubmit }: { onSubmit: (slug: string) => void }) {
  const [slug, setSlug] = useState('');
  const clean = slug.trim().toLowerCase();
  const base = issuerBase();
  const preview = clean ? issuerForHub(clean) : undefined;

  const submit = () => {
    if (clean) onSubmit(clean);
  };

  return (
    <div
      style={{
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--ol-bg-base)',
        padding: 24,
      }}
    >
      <div
        style={{
          width: 420,
          maxWidth: '100%',
          background: 'var(--ol-bg-container)',
          border: '1px solid var(--ol-border)',
          borderRadius: 12,
          padding: 28,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <DeploymentUnitOutlined style={{ fontSize: 26, color: 'var(--ol-primary)' }} />
          <Title level={4} style={{ margin: 0 }}>
            OneLattice
          </Title>
        </div>
        <Text type="secondary" style={{ fontSize: 13 }}>
          Sign in to your hub. Enter your hub slug — you’ll be redirected to your identity provider.
        </Text>

        <div style={{ marginTop: 18 }}>
          <label htmlFor="hub-slug" style={{ fontSize: 12, fontWeight: 600, color: 'var(--ol-text-secondary)' }}>
            Hub slug
          </label>
          <Input
            id="hub-slug"
            size="large"
            autoFocus
            placeholder="e.g. acme"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            onPressEnter={submit}
            style={{ marginTop: 6 }}
            addonBefore="opsflow-"
          />
          {preview && (
            <Text type="secondary" className="ol-mono" style={{ fontSize: 10.5, display: 'block', marginTop: 6 }}>
              → {preview}
            </Text>
          )}
        </div>

        {!base && (
          <Alert
            type="warning"
            showIcon
            style={{ marginTop: 14 }}
            message="Identity provider not configured"
            description="VITE_OIDC_ISSUER_BASE is not set for this build. The deploy must set it (e.g. https://auth.opsflow.linkzar.com)."
          />
        )}

        <Button
          type="primary"
          size="large"
          block
          icon={<LoginOutlined />}
          disabled={!clean || !base}
          onClick={submit}
          style={{ marginTop: 18 }}
        >
          Continue to sign in
        </Button>
      </div>
    </div>
  );
}
